<template>
  <div class="auth-container">
    <div class="auth-card card">
      <div class="card-body p-8">
        <!-- Logo and Branding -->
        <div class="text-center mb-8">
          <div class="auth-logo">
            <svg
              class="h-12 w-12 mx-auto text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <h1 class="auth-title">MCP Tools Collection</h1>
          <p class="auth-subtitle">AI-Powered Modular Architecture</p>
        </div>

        <!-- Authentication States -->
        <div v-if="!authToken && !isProcessing" class="space-y-6">
          <!-- Welcome Message -->
          <div class="text-center space-y-4">
            <h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Welcome to MCP Tools
            </h2>
            <p class="text-gray-600 dark:text-gray-400">
              Sign in with your Telegram account to access powerful AI tools and services.
            </p>
          </div>

          <!-- Telegram Login Button -->
          <div class="space-y-4">
            <button
              :disabled="isLoading"
              class="w-full btn-telegram flex items-center justify-center space-x-3 py-4 text-lg font-medium"
              @click="initiateAuthFlow"
            >
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm5.568 8.16c-.18 1.896-.96 6.504-1.356 8.628-.168.9-.504 1.2-.816 1.236-.696.06-1.224-.456-1.896-.9-1.056-.696-1.656-1.128-2.676-1.8-1.188-.78-.42-1.212.264-1.908.18-.18 3.252-2.976 3.312-3.228a.24.24 0 0 0-.06-.216c-.072-.06-.168-.036-.252-.024-.108.024-1.788 1.14-5.064 3.348-.48.336-.912.492-1.296.48-.432-.012-1.248-.24-1.86-.444-.756-.24-1.344-.372-1.296-.792.024-.216.324-.432.888-.66 3.504-1.524 5.832-2.532 6.996-3.012 3.336-1.392 4.02-1.632 4.476-1.632.096 0 .324.024.468.144.12.096.156.228.168.324-.012.072-.012.288-.024.408z"
                />
              </svg>
              <span v-if="!isLoading">Continue with Telegram</span>
              <span v-else class="flex items-center">
                <div class="loading-spinner mr-2" />
                Connecting...
              </span>
            </button>

            <!-- Alternative Flow Info -->
            <div class="text-center text-sm text-gray-500 dark:text-gray-400">
              <p>Don't have our Telegram bot yet?</p>
              <a
                :href="telegramBotUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary-600 hover:text-primary-700 font-medium"
              >
                Start a conversation with @{{ botUsername }}
              </a>
            </div>
          </div>

          <!-- Features Preview -->
          <div class="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 text-center">
              What you'll get access to:
            </h3>
            <div class="grid grid-cols-1 gap-4">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <svg class="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <span class="text-gray-700 dark:text-gray-300">AI-powered web search and analysis</span>
              </div>
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <svg class="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <span class="text-gray-700 dark:text-gray-300">Advanced calculator and mathematical tools</span>
              </div>
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <svg class="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <span class="text-gray-700 dark:text-gray-300">Document processing and vector search</span>
              </div>
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <svg class="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <span class="text-gray-700 dark:text-gray-300">Secure multi-tenant data isolation</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Processing State -->
        <div v-else-if="isProcessing" class="text-center space-y-6">
          <div class="flex justify-center">
            <div class="loading-spinner w-12 h-12 border-primary-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {{ processingMessage }}
            </h2>
            <p class="text-gray-600 dark:text-gray-400">This should only take a moment...</p>
          </div>
        </div>

        <!-- Error State -->
        <div
          v-if="error"
          class="mt-6 p-4 bg-error-50 dark:bg-error-900/50 border border-error-200 dark:border-error-800 rounded-lg"
        >
          <div class="flex items-center">
            <svg class="w-5 h-5 text-error-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="text-error-700 dark:text-error-200 font-medium">{{ error }}</span>
          </div>
          <button
            class="mt-3 text-sm text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300"
            @click="clearError"
          >
            Try again
          </button>
        </div>

        <!-- Success State -->
        <div
          v-if="successMessage"
          class="mt-6 p-4 bg-success-50 dark:bg-success-900/50 border border-success-200 dark:border-success-800 rounded-lg"
        >
          <div class="flex items-center">
            <svg class="w-5 h-5 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="text-success-700 dark:text-success-200">{{ successMessage }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Help Section -->
    <div class="mt-8 text-center">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Need help?
        <a href="mailto:support@profikid.nl" class="text-primary-600 hover:text-primary-700">
          Contact Support
        </a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { storageService } from '@/services/storage';

const router = useRouter();
const authStore = useAuthStore();

// State
const isLoading = ref(false);
const isProcessing = ref(false);
const error = ref('');
const successMessage = ref('');
const processingMessage = ref('Authenticating with Telegram...');
const authToken = ref('');

// Configuration
const botUsername = ref(import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'Profikidmcpbot');
const telegramBotUrl = ref(`https://t.me/${botUsername.value}?start=auth`);

// Methods
const initiateAuthFlow = () => {
  isLoading.value = true;
  error.value = '';

  try {
    // Generate a unique session identifier for this auth attempt
    const sessionId = generateSessionId();

    // Store session ID for validation
    storageService.setTemporaryItem('auth_session_id', sessionId);

    // Create the Telegram bot URL with the session ID
    const authUrl = `https://t.me/${botUsername.value}?start=auth_${sessionId}`;

    // Open Telegram in a new window/tab
    window.open(authUrl, '_blank', 'noopener,noreferrer');

    // Start polling for authentication result
    startAuthPolling(sessionId);
  } catch (err) {
    console.error('Failed to initiate auth flow:', err);
    error.value = 'Failed to initiate authentication. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const generateSessionId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const startAuthPolling = (_sessionId: string) => {
  isProcessing.value = true;
  processingMessage.value = 'Waiting for Telegram authentication...';

  const maxAttempts = 60; // 5 minutes with 5-second intervals
  let attempts = 0;

  const pollInterval = setInterval(async () => {
    attempts++;

    try {
      // Check if auth token is available in storage
      const token = storageService.getAuthToken();

      if (token) {
        clearInterval(pollInterval);
        await processAuthToken(token);
        // Keep persistent auth token - don't clear it
        return;
      }

      // Update processing message
      if (attempts > 20) {
        processingMessage.value = 'Still waiting for authentication...';
      }

      // Timeout after max attempts
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        isProcessing.value = false;
        error.value = 'Authentication timeout. Please try again.';
      }
    } catch (err) {
      console.error('Auth polling error:', err);
      clearInterval(pollInterval);
      isProcessing.value = false;
      error.value = 'Authentication failed. Please try again.';
    }
  }, 5000); // Poll every 5 seconds
};

const processAuthToken = async (token: string): Promise<boolean> => {
  try {
    processingMessage.value = 'Completing authentication...';

    const result = await authStore.login(token);

    if (result.success) {
      successMessage.value = 'Authentication successful! Redirecting...';

      // Wait a moment for user to see success message
      setTimeout(async () => {
        // Check for intended route
        const intendedRoute = storageService.consumeIntendedRoute();
        if (intendedRoute) {
          await router.push(intendedRoute);
        } else {
          await router.push({ name: 'dashboard' });
        }
      }, 1500);

      return true;
    } else {
      error.value = result.error || 'Authentication failed';
      return false;
    }
  } catch (err) {
    console.error('Auth processing error:', err);
    error.value = 'Authentication processing failed. Please try again.';
    return false;
  } finally {
    isProcessing.value = false;
  }
};

const clearError = () => {
  error.value = '';
  authStore.clearError();
};

// Check for auth token on mount
onMounted(async () => {
  // If already authenticated, redirect to dashboard
  if (authStore.isAuthenticated) {
    const intendedRoute = storageService.consumeIntendedRoute();
    if (intendedRoute) {
      await router.push(intendedRoute);
    } else {
      await router.push({ name: 'dashboard' });
    }
    return;
  }

  // Check for auth token from URL or storage
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');
  const tokenFromStorage = storageService.getAuthToken();

  const token = tokenFromUrl || tokenFromStorage;

  if (token) {
    authToken.value = token;
    await processAuthToken(token);

    // Clean up URL regardless of auth success/failure
    if (tokenFromUrl) {
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    // Keep persistent auth token - don't clear it
  }
});

// Watch for auth store errors
watch(
  () => authStore.error,
  authError => {
    if (authError && !error.value) {
      error.value = authError.message;
      isProcessing.value = false;
    }
  }
);

// Watch for authentication changes
watch(
  () => authStore.isAuthenticated,
  isAuthenticated => {
    if (isAuthenticated) {
      // User became authenticated, redirect appropriately
      const intendedRoute = storageService.consumeIntendedRoute();
      if (intendedRoute) {
        router.push(intendedRoute);
      } else {
        router.push({ name: 'dashboard' });
      }
    }
  }
);

// Handle browser back/forward
window.addEventListener('popstate', () => {
  if (authStore.isAuthenticated) {
    const intendedRoute = storageService.consumeIntendedRoute();
    if (intendedRoute) {
      router.push(intendedRoute);
    } else {
      router.push({ name: 'dashboard' });
    }
  }
});

// Set page title
document.title = 'Sign In | MCP Tools Collection';
</script>

<style scoped>
.auth-container {
  background: linear-gradient(
    135deg,
    theme('colors.primary.50') 0%,
    theme('colors.blue.50') 50%,
    theme('colors.purple.50') 100%
  );
}

.dark .auth-container {
  background: linear-gradient(
    135deg,
    theme('colors.gray.900') 0%,
    theme('colors.gray.800') 50%,
    theme('colors.gray.900') 100%
  );
}

.auth-card {
  backdrop-filter: blur(10px);
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

.dark .auth-card {
  background: rgba(31, 41, 55, 0.8);
  border: 1px solid rgba(75, 85, 99, 0.3);
}

.btn-telegram {
  background: linear-gradient(135deg, #0088cc 0%, #005580 100%);
  transition: all 0.2s ease;
}

.btn-telegram:hover:not(:disabled) {
  background: linear-gradient(135deg, #0077b3 0%, #004466 100%);
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(0, 136, 204, 0.3);
}

.btn-telegram:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .btn-telegram:hover:not(:disabled) {
    transform: none;
  }

  .loading-spinner {
    animation: none;
  }
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .auth-container {
    padding: 1rem;
  }

  .auth-card {
    margin: 0;
  }
}
</style>
